import { VOLUME_CONVERSIONS, WEIGHT_CONVERSIONS } from '../constants/conversions.js';
/** Units that represent volume */
const VOLUME_UNITS = new Set(['ml', 'cl', 'l', 'tsp', 'tbsp', 'cup', 'fl_oz']);
/** Units that represent weight */
const WEIGHT_UNITS = new Set(['g', 'kg', 'oz', 'lb']);
/** Non-convertible units (countable / informal) */
const NON_CONVERTIBLE = new Set([
    'other',
    'piece',
    'pinch',
    'bunch',
    'slice',
    'clove',
]);
/**
 * Convert a value from one unit to another.
 *
 * Supports:
 * - Volume ↔ Volume (exact)
 * - Weight ↔ Weight (exact)
 * - Weight ↔ Volume (approximate, requires density data)
 *
 * @returns The converted value, or null if the conversion is not possible.
 */
export function convertUnit(params) {
    const { value, from, to, density } = params;
    if (from === to) {
        return { value, unit: to, approximate: false };
    }
    if (NON_CONVERTIBLE.has(from) || NON_CONVERTIBLE.has(to))
        return null;
    const fromIsVolume = VOLUME_UNITS.has(from);
    const fromIsWeight = WEIGHT_UNITS.has(from);
    const toIsVolume = VOLUME_UNITS.has(to);
    const toIsWeight = WEIGHT_UNITS.has(to);
    if ((fromIsVolume && toIsVolume) || (fromIsWeight && toIsWeight)) {
        const table = fromIsVolume ? VOLUME_CONVERSIONS : WEIGHT_CONVERSIONS;
        const entry = table.find((e) => e.from === from && e.to === to);
        if (entry) {
            return { value: value * entry.factor, unit: to, approximate: false };
        }
        const baseUnit = fromIsVolume ? 'ml' : 'g';
        const toBase = table.find((e) => e.from === from && e.to === baseUnit);
        const fromBase = table.find((e) => e.from === baseUnit && e.to === to);
        if (toBase && fromBase) {
            return { value: value * toBase.factor * fromBase.factor, unit: to, approximate: false };
        }
        return null;
    }
    if (fromIsWeight && toIsVolume) {
        const grams = convertToBase(value, from, 'g', WEIGHT_CONVERSIONS);
        if (grams === null)
            return null;
        const result = gramsToVolume(grams, to, density);
        if (result === null)
            return null;
        return { value: result, unit: to, approximate: true };
    }
    if (fromIsVolume && toIsWeight) {
        const grams = volumeToGrams(value, from, density);
        if (grams === null)
            return null;
        const result = convertFromBase(grams, 'g', to, WEIGHT_CONVERSIONS);
        if (result === null)
            return null;
        return { value: result, unit: to, approximate: true };
    }
    return null;
}
/**
 * Whether the reference data carries enough information to cross the
 * weight ↔ volume boundary for this ingredient.
 */
export function hasDensityData(density) {
    return effectiveDensity(density) !== null;
}
/**
 * Get all compatible target units for a given source unit.
 * Returns all units that can be converted to, with cross-type units only if density is available.
 */
export function getCompatibleUnits(from, density) {
    if (NON_CONVERTIBLE.has(from))
        return [];
    const fromIsVolume = VOLUME_UNITS.has(from);
    const fromIsWeight = WEIGHT_UNITS.has(from);
    const sameType = fromIsVolume
        ? [...VOLUME_UNITS].filter((u) => u !== from)
        : [...WEIGHT_UNITS].filter((u) => u !== from);
    const crossType = hasDensityData(density)
        ? fromIsWeight
            ? [...VOLUME_UNITS]
            : fromIsVolume
                ? [...WEIGHT_UNITS]
                : []
        : [];
    return [...sameType, ...crossType];
}
/**
 * Get all conversions for a value from one unit.
 * Returns an array of ConversionResult for every compatible target unit.
 */
export function getAllConversions(params) {
    const { value, from, density } = params;
    const targets = getCompatibleUnits(from, density);
    const results = [];
    for (const to of targets) {
        const result = convertUnit({ value, from, to, density });
        if (result) {
            results.push(result);
        }
    }
    return results;
}
/**
 * Formats a converted value for display with smart rounding.
 * Rules:
 * - Values >= 100: no decimals (e.g. "250")
 * - Values >= 10: 1 decimal (e.g. "15.5")
 * - Values >= 0.01: 2 decimals (e.g. "2.33", "0.25")
 * - Smaller values: 2 significant digits, so they never collapse to "0"
 * - Trailing zeros are removed
 */
export function formatConvertedValue(value) {
    let formatted;
    if (value >= 100) {
        formatted = Math.round(value).toString();
    }
    else if (value >= 10) {
        formatted = value.toFixed(1);
    }
    else if (value >= 0.01 || value <= 0) {
        formatted = value.toFixed(2);
    }
    else {
        formatted = value.toPrecision(2);
    }
    if (formatted.includes('.')) {
        formatted = formatted.replace(/0+$/, '').replace(/\.$/, '');
    }
    return formatted;
}
/**
 * Formats a conversion for display, prefixing approximate results with "≈".
 * Weight ↔ volume conversions depend on the ingredient density and are never exact.
 */
export function formatConversionResult(result) {
    const formatted = formatConvertedValue(result.value);
    return result.approximate ? `≈ ${formatted}` : formatted;
}
/** Millilitres in one unit of the spoon/cup measures the reference data weighs. */
const ML_PER_UNIT = {
    tbsp: VOLUME_CONVERSIONS.find((e) => e.from === 'tbsp' && e.to === 'ml')?.factor,
    cup: VOLUME_CONVERSIONS.find((e) => e.from === 'cup' && e.to === 'ml')?.factor,
};
/**
 * Grams of one unit of `unit` when the reference data weighs that measure directly
 * (flour: 1 tbsp = 8 g, 1 cup = 125 g). More faithful than a generic density,
 * because a spooned powder does not pack like a poured liquid.
 */
function directGramsPerUnit(unit, density) {
    if (unit === 'tbsp' && density?.tablespoon_g)
        return density.tablespoon_g;
    if (unit === 'cup' && density?.cup_g)
        return density.cup_g;
    return null;
}
/**
 * Grams per millilitre for this ingredient: the explicit density when known,
 * otherwise derived from whichever spoon/cup weight the reference data carries.
 */
function effectiveDensity(density) {
    if (density?.g_per_ml)
        return density.g_per_ml;
    if (density?.tablespoon_g && ML_PER_UNIT.tbsp)
        return density.tablespoon_g / ML_PER_UNIT.tbsp;
    if (density?.cup_g && ML_PER_UNIT.cup)
        return density.cup_g / ML_PER_UNIT.cup;
    return null;
}
/** Weight of a volume, using the ingredient's own spoon/cup weight when there is one. */
function volumeToGrams(value, from, density) {
    const direct = directGramsPerUnit(from, density);
    if (direct !== null)
        return value * direct;
    const ml = convertToBase(value, from, 'ml', VOLUME_CONVERSIONS);
    const gPerMl = effectiveDensity(density);
    if (ml === null || gPerMl === null)
        return null;
    return ml * gPerMl;
}
/** Volume of a weight, using the ingredient's own spoon/cup weight when there is one. */
function gramsToVolume(grams, to, density) {
    const direct = directGramsPerUnit(to, density);
    if (direct !== null)
        return grams / direct;
    const gPerMl = effectiveDensity(density);
    if (gPerMl === null)
        return null;
    return convertFromBase(grams / gPerMl, 'ml', to, VOLUME_CONVERSIONS);
}
function convertToBase(value, from, base, table) {
    if (from === base)
        return value;
    const entry = table.find((e) => e.from === from && e.to === base);
    return entry ? value * entry.factor : null;
}
function convertFromBase(value, base, to, table) {
    if (base === to)
        return value;
    const entry = table.find((e) => e.from === base && e.to === to);
    return entry ? value * entry.factor : null;
}
//# sourceMappingURL=convert-units.js.map